import { Application, Container, Graphics, Text } from 'pixi.js';
import { theme } from '../config/theme';
import { beaconDefs } from '../config/beacons';
import { Beacon } from './beacon';
import { Agent } from './agent';
import type { AgentDef } from './agent';
import { CredentialPopup } from './credential-popup';
import { EventBus } from '../event/bus';
import type { AgentCredEvent } from '../event/types';

export class NetworkGraph {
  public app: Application;
  private layerAgents!: Container;
  private layerBeacons!: Container;
  private layerTrails!: Graphics;
  private layerPopups!: Container;
  private hud!: Graphics;
  private hudText!: Text;
  public beacons: Beacon[] = [];
  private agents: Agent[] = [];
  private popups: Array<{ popup: import('./credential-popup').CredentialPopup; sprite: Container }> = [];
  private eventBus: EventBus;
  private width: number;
  private height: number;

  constructor(canvas: HTMLCanvasElement, eventBus: EventBus) {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.eventBus = eventBus;

    this.app = new Application();

    // Initialize async
    this.init(canvas);

    window.addEventListener('resize', () => this.resize());
    eventBus.on((e: AgentCredEvent) => this.handleEvent(e));
  }

  private async init(canvas: HTMLCanvasElement) {
    await this.app.init({
      canvas,
      width: this.width,
      height: this.height,
      backgroundColor: theme.colors.bg,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    this.layerTrails = new Graphics();
    this.app.stage.addChild(this.layerTrails);

    this.layerBeacons = new Container();
    this.app.stage.addChild(this.layerBeacons);

    this.layerAgents = new Container();
    this.app.stage.addChild(this.layerAgents);

    this.layerPopups = new Container();
    this.app.stage.addChild(this.layerPopups);

    this.hud = new Graphics();
    this.app.stage.addChild(this.hud);

    this.hudText = new Text({
      text: 'AGENTCRED BEACON',
      style: {
        fontFamily: theme.fonts.mono,
        fontSize: 11,
        fill: theme.colors.hud,
      },
    });
    this.hudText.x = 16;
    this.hudText.y = 12;
    this.app.stage.addChild(this.hudText);

    this.drawGrid();
    this.spawnBeacons();
    this.spawnGhosts(40);

    this.app.ticker.add(() => this.tick());
  }

  private drawGrid() {
    const g = new Graphics();
    g.stroke({ color: theme.colors.grid, width: 1 });
    const spacing = 80;
    for (let x = 0; x < this.width; x += spacing) {
      g.moveTo(x, 0);
      g.lineTo(x, this.height);
    }
    for (let y = 0; y < this.height; y += spacing) {
      g.moveTo(0, y);
      g.lineTo(this.width, y);
    }
    g.stroke();
    this.app.stage.addChildAt(g, 0);
  }

  private spawnBeacons() {
    for (const def of beaconDefs) {
      const b = new Beacon(def, this.width, this.height);
      this.layerBeacons.addChild(b);
      this.beacons.push(b);
    }
  }

  private spawnGhosts(count: number) {
    for (let i = 0; i < count; i++) {
      const def: AgentDef = {
        did: `did:agentcred:ghost:${Math.random().toString(36).slice(2, 8)}`,
        kind: 'ghost',
        trustScore: Math.floor(Math.random() * 60) + 10,
        credentials: ['read:profile'],
        x: Math.random() * this.width,
        y: Math.random() * this.height,
      };
      const agent = new Agent(def);
      this.pickRandomBeacon(agent);
      this.layerAgents.addChild(agent);
      this.agents.push(agent);
    }
  }

  public addRealAgent(def: AgentDef): Agent {
    const agent = new Agent(def);
    this.layerAgents.addChild(agent);
    this.agents.push(agent);
    return agent;
  }

  private pickRandomBeacon(agent: Agent) {
    const b = this.beacons[Math.floor(Math.random() * this.beacons.length)];
    agent.setTarget(b);
  }

  private handleEvent(e: AgentCredEvent) {
    // Ghost events or real agent events
    if (e.type === 'GhostHit' && e.targetAuthority) {
      const beacon = this.beacons.find(b => b.def.authorityId === e.targetAuthority);
      if (beacon) beacon.flash(e.result === 'granted');
    }
    else if (e.type === 'CredentialVerified' && e.agentDid && e.targetAuthority) {
      const beacon = this.beacons.find(b => b.def.authorityId === e.targetAuthority);
      if (beacon) beacon.flash(e.result === 'granted');
    }
  }

  private tick() {
    // Update beacons
    for (const b of this.beacons) b.tick(1);

    // Update agents
    for (const agent of this.agents) {
      agent.tick(1);

      // Ghost agent checks
      if (agent.kind === 'ghost' && agent.isAtBeacon()) {
        const beacon = agent.targetBeacon;
        if (beacon) {
          const granted = Math.random() < 0.8;
          beacon.flash(granted);
          this.eventBus.emit({
            type: 'GhostHit',
            timestamp: Date.now(),
            targetAuthority: beacon.def.authorityId,
            result: granted ? 'granted' : 'denied',
          });
        }
        // Pick next beacon after dwell
        this.pickRandomBeacon(agent);
      }
    }

    // Agent proximity → credential exchange popups
    const exchangeDistance = 50;
    const exchanged = new Set<string>();
    for (let i = 0; i < this.agents.length; i++) {
      for (let j = i + 1; j < this.agents.length; j++) {
        const a = this.agents[i];
        const b = this.agents[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < exchangeDistance) {
          const pairKey = a.did < b.did ? `${a.did}:${b.did}` : `${b.did}:${a.did}`;
          if (!exchanged.has(pairKey) && Math.random() < 0.02) {
            exchanged.add(pairKey);
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;
            const claims = [
              `did:${a.did.slice(-6)}`,
              `trust:${a.trustScore}`,
              `scope:read`,
            ];
            const popup = new CredentialPopup(claims, midX, midY);
            this.layerPopups.addChild(popup);
            this.popups.push({ popup, sprite: popup });
          }
        }
      }
    }

    // Update popups, remove dead ones
    this.popups = this.popups.filter(p => {
      const alive = p.popup.tick();
      if (!alive) {
        this.layerPopups.removeChild(p.sprite);
      }
      return alive;
    });

    // Draw trails
    this.layerTrails.clear();
    for (const agent of this.agents) {
      agent.drawTrail(this.layerTrails, this.width, this.height);
    }

    // Update HUD
    const ghostCount = this.agents.filter(a => a.kind === 'ghost').length;
    const realCount = this.agents.filter(a => a.kind === 'real').length;
    this.hudText.text = `AGENTCRED BEACON  |  Ghosts: ${ghostCount}  Real: ${realCount}`;
  }

  private resize() {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.app.renderer.resize(this.width, this.height);
    // Re-position beacons for new size
    for (const b of this.beacons) {
      b.x = b.def.x * this.width;
      b.y = b.def.y * this.height;
    }
  }
}
